import { createSelector } from "@reduxjs/toolkit";
import { determineNotify, iAmAdmin, isRootEffice } from "helpers/efficeHelpers";
import { selectCurrentActive, selectCurrentRoot } from "./efficeTreeSlice";
import { selectCurrentSpaceId } from "./spaceSlice";

// Effice Types:
export const MESSAGE = "message";
export const BRANCH = "branch";
export const EFFICE = "effice";

export const selectCurrentActiveEffice = createSelector(
  (state) => state.efficeTree.currentActive,
  (state) => state.efficeCache.data,
  (currentActive, data) => data[currentActive]
);

export const selectCurrentRootEffice = createSelector(
  (state) => selectCurrentRoot(state),
  (state) => state.efficeCache.data,
  (currentRoot, data) => data[currentRoot]
);

export const selectCurrentActiveType = (state) => {
  let eff = selectCurrentActiveEffice(state);
  if (!eff) return null;
  if (isRootEffice(eff)) return EFFICE;
  if (eff.pinned) return BRANCH;
  return MESSAGE;
};

export const selectCurrentPID = createSelector(
  (state) => state.efficeCache.data,
  (state) => state.efficeTree.currentActive,
  (data, currentActive) => data[currentActive]?.permissions_id
);

export const selectCurrentPermissionsEffice = createSelector(
  (state) => state.efficeCache.data,
  (state) => selectCurrentPID(state),
  (data, currentPID) => data[currentPID]
);

export const selectCurrentAdmin = createSelector(
  (state) => selectCurrentPermissionsEffice(state),
  (permissionsEffice) => iAmAdmin(permissionsEffice)
);

export const selectCurrentAppId = createSelector(
  (state) => selectCurrentActiveEffice(state),
  (currentEffice) => currentEffice?.app_id
);

export const selectCurrentAID = createSelector(
  (state) => state.efficeCache.data,
  (state) => selectCurrentPermissionsEffice(state)?.parent_id,
  (data, permParentId) =>
    permParentId ? data[permParentId]?.permissions_id : null
);

export const selectCurrentAncestorEffice = createSelector(
  (state) => state.efficeCache.data,
  (state) => selectCurrentAID(state),
  (data, currentAID) => (currentAID ? data[currentAID] : null)
);

export const selectInThisSpace = createSelector(
  (state) => selectCurrentSpaceId(state),
  (state) => selectCurrentActive(state),
  (spaceId, currentActive) =>
    spaceId && currentActive && spaceId === currentActive
);

export const makeSelectNotify = () =>
  createSelector(
    (state) => state.efficeCache.data,
    (state) => state.myReadEffices.reads,
    (_, id) => id,
    (data, reads, id) => determineNotify(data[id].sub_effices, reads[id])
  );

export const makeSelectPermissionsNode = () =>
  createSelector(
    (state) => state.efficeCache.data,
    (_, id) => id,
    (data, id) =>
      id && data[id] && data[data[id].permissions_id]
        ? data[data[id].permissions_id]
        : null
  );

export const makeSelectAncestorNode = () =>
  createSelector(
    (state) => state.efficeCache.data,
    (_, id) => id,
    (data, id) =>
      id && // Current Node Id
      data[id] && // Current Node
      data[data[id].permissions_id] && // Permissions Node
      data[data[id].permissions_id].parent_id && // Permissions Node's Parent_Id
      data[data[data[id].permissions_id].parent_id] // Permissions Node's Parent-Node
        ? data[data[data[data[id].permissions_id].parent_id].permissions_id] // Ancestor-Node
        : null
  );
